import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import React, { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  AttendanceDashboardPeriod,
  AttendanceDashboardPeriodPreset,
  createDatePeriod,
  createRangePeriod,
  formatDashboardPeriodLabel,
  getLastMonthPeriod,
  getLastWeekPeriod
} from 'utils/attendanceDashboardPeriod';
import './index.scss';

interface Props {
  period: AttendanceDashboardPeriod | null;
  onChange: (period: AttendanceDashboardPeriod) => void;
  onClear?: () => void;
  className?: string;
  includeDate?: boolean;
  maxDate?: Date;
}

const PRESETS: { id: AttendanceDashboardPeriodPreset; label: string }[] = [
  { id: 'date', label: 'Date' },
  { id: 'last-week', label: 'Last week' },
  { id: 'last-month', label: 'Last month' },
  { id: 'range', label: 'Range' }
];

export const AttendanceDashboardPeriodFilter = ({
  period,
  onChange,
  onClear,
  className,
  includeDate = false,
  maxDate
}: Props) => {
  const presets = includeDate
    ? PRESETS
    : PRESETS.filter((preset) => preset.id !== 'date');

  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [menuPreset, setMenuPreset] =
    useState<AttendanceDashboardPeriodPreset | null>(period?.preset ?? null);
  const [draftFrom, setDraftFrom] = useState<Date | null>(period?.from ?? null);
  const [draftTo, setDraftTo] = useState<Date | null>(period?.to ?? null);
  const showClear = !!onClear && !!period;

  useEffect(() => {
    if (!open) {
      setMenuPreset(period?.preset ?? null);
    }
  }, [open, period?.preset]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const applyPreset = (preset: AttendanceDashboardPeriodPreset) => {
    setMenuPreset(preset);

    if (preset === 'last-week') {
      onChange(getLastWeekPeriod());
      setOpen(false);
      return;
    }

    if (preset === 'last-month') {
      onChange(getLastMonthPeriod());
      setOpen(false);
      return;
    }

    setDraftFrom(null);
    setDraftTo(null);
  };

  const clear = () => {
    onClear?.();
    setMenuPreset(null);
    setOpen(false);
  };

  const applyDate = (date: Date | null) => {
    if (!date) return;
    onChange(createDatePeriod(date));
    setOpen(false);
  };

  const handleToggleOpen = () => {
    setOpen((current) => {
      const next = !current;
      if (next && period?.preset === 'range') {
        setMenuPreset('range');
        setDraftFrom(null);
        setDraftTo(null);
      }
      return next;
    });
  };

  const applyRange = () => {
    if (!draftFrom || !draftTo) return;
    onChange(createRangePeriod(draftFrom, draftTo));
    setOpen(false);
  };

  return (
    <div
      className={`attendance-period-filter${
        open ? ' attendance-period-filter--open' : ''
      }${showClear ? ' attendance-period-filter--clearable' : ''}${
        className ? ` ${className}` : ''
      }`}
      ref={containerRef}
    >
      <button
        type="button"
        className="attendance-period-filter__trigger"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={handleToggleOpen}
      >
        <CalendarMonthOutlinedIcon fontSize="small" aria-hidden />
        <span className="attendance-period-filter__trigger-label">
          {period ? formatDashboardPeriodLabel(period) : 'Any date'}
        </span>
        <KeyboardArrowDownIcon
          className={`attendance-period-filter__chevron${
            open ? ' attendance-period-filter__chevron--open' : ''
          }`}
          fontSize="small"
          aria-hidden
        />
      </button>

      {showClear && (
        <button
          type="button"
          className="attendance-period-filter__clear"
          aria-label="Clear date filter"
          title="Clear date filter"
          onClick={clear}
        >
          <CloseIcon fontSize="small" aria-hidden />
        </button>
      )}

      {open && (
        <div
          className={`attendance-period-filter__menu${
            menuPreset === 'range' || menuPreset === 'date'
              ? ' attendance-period-filter__menu--range'
              : ''
          }`}
        >
          <div className="attendance-period-filter__options" role="listbox">
            {onClear && (
              <button
                type="button"
                role="option"
                aria-selected={menuPreset === null}
                className={`attendance-period-filter__option${
                  menuPreset === null
                    ? ' attendance-period-filter__option--active'
                    : ''
                }`}
                onClick={clear}
              >
                Any date
              </button>
            )}
            {presets.map((preset) => (
              <button
                key={preset.id}
                type="button"
                role="option"
                aria-selected={menuPreset === preset.id}
                className={`attendance-period-filter__option${
                  menuPreset === preset.id
                    ? ' attendance-period-filter__option--active'
                    : ''
                }`}
                onClick={() => applyPreset(preset.id)}
              >
                {preset.label}
              </button>
            ))}
          </div>

          {menuPreset === 'date' && (
            <div className="attendance-period-filter__range">
              <span className="attendance-period-filter__calendar-label">
                Select a date
              </span>
              <DatePicker
                inline
                selected={period?.preset === 'date' ? period.from : null}
                maxDate={maxDate}
                disabledKeyboardNavigation
                onChange={(date) => applyDate(date as Date | null)}
              />
            </div>
          )}

          {menuPreset === 'range' && (
            <div className="attendance-period-filter__range">
              <span className="attendance-period-filter__calendar-label">
                Select start and end date
              </span>
              <DatePicker
                inline
                selectsRange
                startDate={draftFrom}
                endDate={draftTo}
                maxDate={maxDate}
                disabledKeyboardNavigation
                onChange={(dates) => {
                  const [start, end] = dates as [Date | null, Date | null];
                  setDraftFrom(start);
                  setDraftTo(end);
                }}
              />
              <button
                type="button"
                className="attendance-period-filter__apply"
                onClick={applyRange}
                disabled={!draftFrom || !draftTo}
              >
                Apply range
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
